import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  LinearProgress,
  Typography,
  Alert,
  Stack,
  Chip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Audiotrack as AudioIcon,
  VideoLibrary as VideoIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { mediaAPI } from '../../services/mediaAPI';

const MediaUploader = ({ 
  onUploadComplete, 
  onDelete, 
  existingMedia = null,
  accept = 'image/*,audio/*,video/*',
  maxSize = 500, // en MB (augmenté à 500MB)
  label = 'Ajouter un média'
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [media, setMedia] = useState(existingMedia);
  const [preview, setPreview] = useState(
    existingMedia?.url && existingMedia.url !== '' 
      ? mediaAPI.getFullUrl(existingMedia.url) 
      : null
  );
  const fileInputRef = useRef(null);

  // Mettre à jour le média et la prévisualisation quand existingMedia change
  useEffect(() => {
    console.log('📸 MediaUploader - existingMedia changed:', existingMedia);
    if (existingMedia && existingMedia.url && existingMedia.url !== '') {
      const fullUrl = mediaAPI.getFullUrl(existingMedia.url);
      console.log('📸 Setting preview URL:', fullUrl);
      setMedia(existingMedia);
      setPreview(fullUrl);
    } else {
      console.log('📸 No existing media, clearing preview');
      setMedia(null);
      setPreview(null);
    }
  }, [existingMedia]);

  const getFileIcon = (mimetype) => {
    if (!mimetype) return <FileIcon />;
    if (mimetype.startsWith('image/')) return <ImageIcon color="primary" />;
    if (mimetype.startsWith('audio/')) return <AudioIcon color="secondary" />;
    if (mimetype.startsWith('video/')) return <VideoIcon color="error" />;
    return <FileIcon />;
  };

  const getFileType = (mimetype) => {
    if (!mimetype) return null;
    if (mimetype.startsWith('image/')) return 'image';
    if (mimetype.startsWith('audio/')) return 'audio';
    if (mimetype.startsWith('video/')) return 'video';
    return null;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validation de la taille
    const fileSizeMB = file.size / 1024 / 1024;
    if (fileSizeMB > maxSize) {
      setError(`Le fichier est trop volumineux (max ${maxSize}MB)`);
      return;
    }

    setError(null);
    setUploading(true);
    setProgress(0);

    try {
      // Créer une prévisualisation locale
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('audio/')) {
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);
      }

      // Upload du fichier
      console.log('📤 Uploading file:', file.name, file.type);
      const response = await mediaAPI.uploadFile(file, setProgress);
      console.log('✅ Upload response:', response);
      
      const uploadedMedia = {
        type: getFileType(response.file.mimetype),
        url: response.file.url,
        filename: response.file.filename,
        size: response.file.size,
        mimetype: response.file.mimetype,
        originalName: response.file.originalName
      };

      console.log('📦 Uploaded media object:', uploadedMedia);
      const fullUrl = mediaAPI.getFullUrl(response.file.url);
      console.log('🖼️ Preview URL:', fullUrl);

      setMedia(uploadedMedia);
      setPreview(fullUrl);
      
      if (onUploadComplete) {
        console.log('🔄 Calling onUploadComplete with:', uploadedMedia);
        onUploadComplete(uploadedMedia);
      }
    } catch (err) {
      console.error('Erreur upload:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'upload');
      setPreview(null);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async () => {
    if (!media) return;

    try {
      if (media.filename) {
        await mediaAPI.deleteFile(media.filename);
      }
      
      setMedia(null);
      setPreview(null);
      
      if (onDelete) {
        onDelete();
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!media && !uploading && (
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={handleClick}
          fullWidth
        >
          {label}
        </Button>
      )}

      {uploading && (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload en cours...
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {progress}%
            </Typography>
          </CardContent>
        </Card>
      )}

      {media && !uploading && (
        <Card>
          {media.type === 'image' && preview && (
            <CardMedia
              component="img"
              height="200"
              image={preview}
              alt="Aperçu"
              sx={{ objectFit: 'contain', bgcolor: 'grey.100' }}
              onError={(e) => {
                console.error('❌ Image failed to load:', preview);
                console.error('Error:', e);
              }}
              onLoad={() => console.log('✅ Image loaded successfully:', preview)}
            />
          )}

          {media.type === 'audio' && preview && (
            <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
              <audio controls style={{ width: '100%' }} src={preview}>
                Votre navigateur ne supporte pas l'élément audio.
              </audio>
            </Box>
          )}

          {media.type === 'video' && preview && (
            <video controls style={{ width: '100%', maxHeight: '300px' }} src={preview}>
              Votre navigateur ne supporte pas l'élément vidéo.
            </video>
          )}

          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                {getFileIcon(media.mimetype)}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {media.originalName || media.filename}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip 
                      label={getFileType(media.mimetype)} 
                      size="small" 
                      variant="outlined"
                    />
                    {media.size && (
                      <Chip 
                        label={formatFileSize(media.size)} 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>
              
              <IconButton 
                color="error" 
                onClick={handleDelete}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MediaUploader;

